import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';

import createGroupAndOpen from './createGroup';
import { CARD, PILL_PRIMARY } from '../Home/HomeDashboard/dashboardStyles';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

const STACK = '@media (max-width: 599.95px)';

const useStyles = makeStyles(() => ({
    card: {
        ...CARD,
        padding: 'clamp(16px, 2.4vw, 26px)',
    },
    form: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        [STACK]: {
            flexDirection: 'column',
            alignItems: 'stretch',
        },
    },
    field: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        boxSizing: 'border-box',
        minHeight: '54px',
        padding: '0 18px',
        border: `1px solid ${REDESIGN_COLORS.line}`,
        borderRadius: '999px',
        background: REDESIGN_COLORS.cream,
        transition: 'border-color 150ms ease, box-shadow 150ms ease',
        '&:focus-within': {
            borderColor: REDESIGN_COLORS.primary,
            boxShadow: '0 0 0 3px rgba(16, 169, 179, 0.15)',
        },
    },
    plus: {
        flexShrink: 0,
        color: REDESIGN_COLORS.primary,
        fontSize: '1.5rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        lineHeight: 1,
    },
    input: {
        flex: 1,
        minWidth: 0,
        border: 0,
        padding: 0,
        background: 'none',
        outline: 'none',
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '1.05rem',
        fontWeight: 600,
        color: REDESIGN_COLORS.dark,
        '&::placeholder': {
            color: REDESIGN_COLORS.muted,
            fontWeight: 500,
            opacity: 1,
        },
    },
    submit: {
        ...PILL_PRIMARY,
        minHeight: '54px',
        padding: '0 26px',
    },
    hint: {
        margin: '12px 0 0 4px',
        color: REDESIGN_COLORS.muted,
        fontSize: '0.86rem',
        fontWeight: 600,
    },
}));

/**
 * The redesigned "start a new group" card: a name field, a submit pill and a
 * hint. Enter or the button creates the group and lands on its board. Shared
 * by the home page and the groups page.
 */
const CreateGroupForm = () => {
    const classes = useStyles();
    const history = useHistory();
    const intl = useIntl();
    const [name, setName] = useState('');
    const [busy, setBusy] = useState(false);

    const submit = (event) => {
        event.preventDefault();
        if (busy || !name.trim()) return;
        setBusy(true);
        // On success the board route replaces this page, so only the failure
        // path needs to re-enable the form.
        createGroupAndOpen(name, history).then((created) => {
            if (!created) setBusy(false);
        });
    };

    const placeholder = intl.formatMessage({ id: 'redesign.home.dashboard.create.placeholder' });

    return (
        <div className={classes.card}>
            <form className={classes.form} onSubmit={submit}>
                <label className={classes.field}>
                    <span aria-hidden="true" className={classes.plus}>
                        +
                    </span>
                    <input
                        type="text"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        placeholder={placeholder}
                        aria-label={placeholder}
                        maxLength={120}
                        disabled={busy}
                        className={classes.input}
                    />
                </label>
                <button type="submit" disabled={busy || !name.trim()} className={classes.submit}>
                    <FormattedMessage id="redesign.CreateGroup.submit" />
                    <span aria-hidden="true">→</span>
                </button>
            </form>
            <p className={classes.hint}>
                <FormattedMessage id="redesign.home.dashboard.create.hint" />
            </p>
        </div>
    );
};

export default CreateGroupForm;
